import type { BlogCard } from './types';

const bookingLink =
  '<div style="text-align:center;margin:0.5rem 0;"><strong><a href="/gastroenterology#booking-calendar-widget">Book a Gastroenterology consultation at LIPS Healthcare</a></strong></div>';

export const acidRefluxBlogCards: BlogCard[] = [
  {
    title: {
      content:
        '### **Acid reflux:** \n\n ## Symptoms, Causes, Tests And What Actually Works',
      styles: {
        h2: 'font-mulish font-bold text-royal-875 mb-0 text-base md:text-lg leading-tight',
        h3: 'font-mulish font-bold text-royal-875 text-base md:text-lg leading-tight',
        strong:
          'font-mulish font-bold text-royal-875 text-base md:text-lg leading-tight',
        p: 'mb-0 mt-3',
      },
      topicsStyles: {
        h2: 'font-mulish text-theme-primary text-3xl md:text-4xl mb-4 md:mb-6 font-black leading-tight line-clamp-2',
        h3: 'font-mulish text-theme-primary text-3xl md:text-4xl mb-4 md:mb-6 pt-2 font-black',
        strong: 'font-mulish text-text-subtitle font-bold',
        p: 'font-mulish mb-0 text-2xl md:text-3xl p-2',
      },
    },
    description: {
      content:
        'Understand acid reflux symptoms, why treatment sometimes fails, how GORD is tested and which treatments can help. Specialist care at LIPS Healthcare, London.',
      styles: {
        p: 'text-sm md:text-base text-text-default font-medium leading-relaxed mb-3',
      },
    },
    metaTitle:
      'Acid Reflux: Symptoms, Causes, Tests & Treatment | LIPS Healthcare',
    imageUrl: 'acid-reflux.webp',
    bannerUrl: 'acid-reflux.webp',
    bannerDescription:
      'Acid reflux occurs when stomach contents travel up into the oesophagus, causing symptoms such as heartburn, regurgitation or chest discomfort that can range from mildly inconvenient to seriously disruptive. LIPS Healthcare offers specialist gastroenterology consultations at Battersea Power Station, London, accessible via the Northern line and London Overground, where Consultant Gastroenterologist Professor Bu’Hussain Hayee provides diagnosis, objective testing and personalised treatment for GORD and related conditions. This article explains what acid reflux is, why standard treatment sometimes fails, how it is properly tested and what the evidence says about treatment.',
    slug: 'acid-reflux-symptoms-causes-tests-treatment',
    contentType: 'article',
    speciality: 'Gastroenterology',
    iconUrl: 'bu-hussain-hayee.webp',
    doctorSlug: 'bu-hussain-hayee',
    date: {
      content: '22, October 2026',
      iconUrl: '/images/blogs/calendar.svg',
      styles: { p: 'text-gray-500 text-xs' },
    },
    author: {
      content: `Professor Bu'Hussain Hayee`,
      styles: { p: 'text-indigo-650 font-bold text-xs' },
    },
    tableOfContents: {
      heading: {
        content: '### TABLE OF CONTENTS:',
        styles: {
          h3: 'font-Mulish font-bold text-royal-875 mb-3 text-lg md:text-xl',
        },
      },
      content: [
        {
          text: 'What is acid reflux and what causes it?',
          id: 'what-is-acid-reflux',
        },
        {
          text: 'Why do acid-suppressing drugs sometimes stop working?',
          id: 'why-acid-suppressing-drugs-stop-working',
        },
        {
          text: 'When acid reflux is not really reflux',
          id: 'when-acid-reflux-is-not-reflux',
        },
        { text: 'How doctors test for acid reflux', id: 'testing-acid-reflux' },
        { text: 'How is acid reflux treated?', id: 'treating-acid-reflux' },
        {
          text: 'Frequently asked questions about acid reflux (FAQs)',
          id: 'frequently-asked-questions',
        },
      ],
      styles: {
        p: 'text-lg text-teal-500 underline leading-snug text-left cursor-pointer font-semibold',
      },
    },
    content: {
      content: [
        `
<span id="what-is-acid-reflux"></span>

### What is acid reflux and what causes it?


Acid reflux happens when stomach contents enter the oesophagus, where they do not belong, usually because the barrier between the stomach and oesophagus is not working properly.

One of the most persistent misunderstandings about acid reflux is the belief that it is caused by too much acid. Your stomach is supposed to contain acid. It is needed for digestion and as part of your body's defence against things you swallow. With gastro-oesophageal reflux disease (GORD), the problem is usually not extraordinary acid production. The problem is that stomach contents are getting into the oesophagus, where they do not belong.
<div align="center">\n\n

![Acid reflux and gastro-oesophageal reflux disease](healthy-gred.webp "600x400")
</div>

That distinction matters. It explains why acid-suppressing drugs work brilliantly for some people and disappoint others completely.

Several factors make reflux more likely. The valve mechanism between the stomach and oesophagus may be weak. There may be a hiatus hernia. The lower oesophageal sphincter can relax at the wrong moment. Abdominal pressure can increase with excess weight or during pregnancy. After eating, acid can also sit near the top of the stomach, close to the gastro-oesophageal junction, positioned conveniently to travel upwards.

True pathological overproduction of gastric acid exists, but conditions such as gastrinoma are rare. For the vast majority of people with GORD, the issue is the barrier rather than excess acid production.

<span id="why-acid-suppressing-drugs-stop-working"></span>

### Why do acid-suppressing drugs sometimes stop working?

Proton pump inhibitors (PPIs) reduce acid but do not necessarily stop reflux, which is why they can fail even when the diagnosis of acid reflux is correct.

PPIs, including omeprazole, lansoprazole, esomeprazole and pantoprazole, are very effective at suppressing stomach acid. But suppressing acid and stopping reflux are not the same thing. Something can still travel upwards from the stomach into the oesophagus. It is simply less acidic.

<div align="center">\n\n
![Specialist assessment of persistent digestive symptoms](ppis.webp "600x400")
</div>

That distinction matters most when regurgitation is the dominant symptom. You can reduce the acidity of the material coming upwards without fixing the mechanical reason it is coming upwards in the first place.

There are several other reasons why treatment may appear to fail. PPIs may not have been taken at the optimal time, as most work best when taken before a meal. The reflux may be weakly acidic or non-acid, in which case acid suppression has less impact. There may be a structural problem such as a significant hiatus hernia. Or the problem may be functional heartburn or oesophageal sensitivity rather than true acid reflux at all. Identifying which applies requires objective testing.

`,
        `

<span id="when-acid-reflux-is-not-reflux"></span>

### When acid reflux is not really reflux

Several conditions cause symptoms that resemble acid reflux but require completely different treatment, which is why accurate diagnosis before escalating therapy matters.

Burning in the chest or upper abdomen can arise from eosinophilic oesophagitis, which causes discomfort, difficulty swallowing and food sticking. Achalasia and other oesophageal motility disorders can produce chest symptoms and regurgitation. Rumination brings food back into the mouth through a different mechanism. Dyspepsia can produce upper abdominal burning that is distinct from true oesophageal reflux.

<div align="center">\n\n
![Indigestion and symptoms that can resemble reflux](reflux-types.webp "600x400")
</div>

Two conditions that deserve particular attention are reflux hypersensitivity and functional heartburn. In reflux hypersensitivity, the amount of reflux is within the normal range, but reflux events correlate with symptoms. The oesophagus appears unusually sensitive to reflux that would not register in most people. Think of the normal oesophageal lining as a brick wall. If that wall becomes more permeable and the nerves behind it become sensitised, something that would barely register in one person produces real burning in another.

In functional heartburn, acid exposure is normal and symptoms do not correlate convincingly with reflux events. The pain is real, generated through genuine sensory pathways, but adding more acid suppression is unlikely to help. Anti-reflux surgery in this group would address entirely the wrong physiology. For patients seen at LIPS Healthcare, Turbine Hall B, Battersea Power Station, Professor Hayee carries out a comprehensive assessment before recommending any intervention.

<b>What about cough, hoarseness and throat symptoms?</b>

Reflux can cause symptoms outside the oesophagus, including chronic cough and hoarseness, but these require careful investigation rather than an automatic reflux diagnosis.

Reflux can be associated with chronic cough, hoarseness, throat-clearing and globus sensation. You will often hear these grouped under laryngopharyngeal reflux (LPR). The difficulty is proving causation. A red or inflamed-looking larynx is not sufficient evidence to diagnose reflux. When heartburn is absent, laryngeal hypersensitivity, allergy, respiratory disease and ENT conditions all require consideration.

<span id="testing-acid-reflux"></span>

### How doctors test for acid reflux

The main tests for acid reflux are gastroscopy, pH monitoring, pH-impedance monitoring and oesophageal manometry, each designed to answer a different diagnostic question.

Most people with straightforward GORD symptoms do not require every available test. Testing becomes important when symptoms are unusual, treatment is failing or an intervention is being considered. Full testing is available at LIPS Healthcare, Turbine Hall B, Battersea Power Station, without an NHS referral or waiting list.

<div align="center">\n\n
![Endoscopy and investigation of digestive symptoms](acid-reflux-test.webp "600x400")
</div>

#### Gastroscopy

Gastroscopy allows direct visual inspection of the oesophagus for inflammation, hiatus hernia, narrowing and Barrett's oesophagus. Biopsies can identify eosinophilic oesophagitis. However, a normal endoscopy does not exclude reflux. Many people with genuine GORD have a completely normal-looking oesophagus.

#### Wireless pH monitoring (Bravo study)

A small wireless capsule measures oesophageal acidity over several days. This is particularly useful when the central question is whether abnormal acid exposure exists. It is a pH test only and does not detect non-acid reflux.

#### pH-impedance monitoring

Impedance monitoring adds the ability to detect the movement of liquid and gas through the oesophagus regardless of acidity. Both acid and non-acid reflux can be examined, and whether reflux episodes correlate with the patient's symptoms can be established. Whether testing is carried out on or off a PPI depends on the specific diagnostic question.

#### Oesophageal manometry

Manometry does not diagnose acid reflux. It measures how the oesophagus contracts and how the lower oesophageal sphincter behaves. It is essential when swallowing symptoms are present, a motility disorder is suspected, or before anti-reflux procedures, as tightening the gastro-oesophageal junction in a patient with oesophageal dysmotility risks worsening symptoms significantly. For further information on investigating and managing GORD, see [NICE guideline CG184](https://www.nice.org.uk/guidance/cg184).

<span id="treating-acid-reflux"></span>

### How is acid reflux treated?

Acid reflux treatment should match the underlying mechanism: PPIs for acid-mediated GORD, behavioural approaches for functional heartburn, and surgical or endoscopic options for structural problems that medication cannot resolve.

For genuine acid-mediated GORD, PPIs remain effective when used correctly. The aim with long-term treatment is the lowest dose that controls the disease. Alginates work differently, forming a physical raft over stomach contents and proving particularly helpful after meals or for breakthrough symptoms.

Lifestyle changes should be individual rather than prescriptive. Weight reduction can substantially improve reflux where excess weight is contributing. Avoiding meals shortly before lying down helps nocturnal reflux. If a specific food reliably causes symptoms, avoiding it makes sense. If it does not, unnecessary dietary restriction adds little benefit.

<div align="center">\n\n
![Gastroenterology care at LIPS Healthcare](reflux-treatment.webp "600x400")
</div>

For reflux hypersensitivity and functional heartburn, neuromodulators at appropriate doses, behavioural approaches and therapies aimed at reducing oesophageal sensitivity are more logical than escalating acid suppression. Rumination and supragastric belching often respond well to diaphragmatic breathing techniques.

For patients with objectively confirmed GORD, persistent symptoms despite optimally used medication, or a preference to avoid long-term tablets, transoral incisionless fundoplication (TIF) offers an endoscopic option. TIF reconstructs the gastro-oesophageal valve from inside the stomach, without abdominal incisions. For patients with a larger hiatus hernia, surgical repair combined with TIF (cTIF) or conventional fundoplication may be more appropriate. Selection matters: neither TIF nor surgery is appropriate for functional heartburn.Patients from Clapham, Vauxhall and Pimlico regularly access this level of specialist gastroenterology care at LIPS Healthcare without a GP referral. 

`,
        `

><span style="font-weight: bold; color: #2A347A;">Medical disclaimer:</span>
>
> This article is for informational purposes only and does not constitute medical advice, diagnosis, or treatment. Always seek the advice of a qualified clinician for any medical condition. Source: NHS guidance on urgent care and walk-in centres, available at nhs.uk. 
<span id="frequently-asked-questions" class="block scroll-mt-28"></span>

`,
      ].join(bookingLink),
      styles: {
        h3: 'text-lg md:text-[32px] font-bold text-theme-primary mb-4',
        h4: 'text-lg md:text-[20px] font-bold text-theme-primary mb-4',
        p: 'text-sm md:text-base text-text-default font-medium leading-relaxed mb-3',
        a: 'font-semibold text-teal-500 underline',
        strong:
          'inline-block bg-theme-primary hover:bg-white border border-theme-primary text-white px-6 py-3 rounded-lg font-bold no-underline cursor-pointer my-4 text-center [&_a]:text-white [&_a]:no-underline [&_a]:font-bold hover:[&_a]:text-theme-primary',
        blockquote:
          'border-l-4 border-theme-primary bg-surface-muted-section rounded-lg p-4 my-8 text-sm text-text-default not-italic',
      },
    },
    faq: {
      heading: {
        content: '### FAQs',
        styles: {
          h3: 'text-xl md:text-2xl font-bold text-theme-primary mb-6',
        },
      },
      items: [
        {
          question: { content: 'Is acid reflux the same as GORD?' },
          answer: {
            content:
              'Acid reflux describes the process of stomach contents travelling upwards into the oesophagus. GORD (gastro-oesophageal reflux disease) is the clinical diagnosis given when reflux causes troublesome symptoms or complications, such as damage to the oesophageal lining.',
          },
        },
        {
          question: { content: 'When should I worry about acid reflux?' },
          answer: {
            content:
              'Seek prompt medical advice for difficulty swallowing, pain on swallowing, unexplained weight loss or persistent vomiting. Vomiting blood or passing black, tarry stools needs urgent medical attention. Call 999 or go to A&E if you are vomiting blood and feel unwell, faint or dizzy, have tummy pain or have black stools. If you have stopped vomiting blood and have no other symptoms, seek an urgent GP appointment or contact NHS 111. See [NHS guidance on vomiting blood](https://www.nhs.uk/symptoms/vomiting-blood/).',
          },
        },
        {
          question: { content: 'What happens if acid reflux goes untreated?' },
          answer: {
            content:
              'Untreated acid reflux can cause oesophagitis, scarring and narrowing of the oesophagus, and Barrett’s oesophagus, a condition where the oesophageal lining changes in a way that carries a small but real risk of developing into oesophageal cancer. Most people with managed reflux do not reach these complications, but repeated uncontrolled acid exposure over years can cause damage that may require endoscopic monitoring and specialist treatment.',
          },
        },
        {
          question: { content: 'Does acid reflux ever go away?' },
          answer: {
            content:
              'Yes, in some cases. Acid reflux triggered by identifiable lifestyle factors such as weight gain, dietary changes or pregnancy can improve or resolve entirely when those factors change. However, gastro-oesophageal reflux disease (GORD) is often a long-term condition requiring ongoing management. For selected patients, endoscopic procedures such as transoral incisionless fundoplication (TIF) can significantly reduce symptoms and the need for medication.',
          },
        },
        {
          question: { content: 'Why am I suddenly getting acid reflux?' },
          answer: {
            content:
              'Sudden onset can be associated with weight gain, a change in diet, a new medication (particularly NSAIDs, aspirin or certain blood pressure drugs), increased stress, or the development of a hiatus hernia. Pregnancy is also a common trigger. If reflux appears suddenly without an obvious cause, or does not respond to initial treatment, a consultant gastroenterologist can investigate for structural causes.',
          },
        },
        {
          question: { content: 'What settles acid reflux quickly?' },
          answer: {
            content:
              'Alginates such as Gaviscon can relieve acid reflux quickly by forming a physical raft over stomach contents, helping prevent them from entering the oesophagus. They act faster than proton pump inhibitors and can be helpful after meals or before bed. Sitting upright and avoiding lying down after eating also helps. PPIs are more effective for long-term acid control but take several days to reach full effect.',
          },
        },
        {
          question: {
            content: 'Can acid reflux cause a chronic cough or sore throat?',
          },
          answer: {
            content:
              'Yes, in some cases, though the link requires careful investigation. Reflux can be associated with chronic cough, hoarseness and throat-clearing. However, a red or inflamed-looking larynx is not sufficient evidence to diagnose reflux as the cause. When heartburn is absent, laryngeal hypersensitivity, allergy, respiratory disease and ENT conditions all require consideration.',
          },
        },
        {
          question: {
            content: 'Why has my acid reflux not improved on omeprazole?',
          },
          answer: {
            content:
              'Several reasons. The PPI may not have been taken at the right time, as taking it before a meal is more effective for most people. The reflux may be weakly acidic or non-acid, reducing the benefit of acid suppression. The underlying problem may be functional heartburn or oesophageal hypersensitivity rather than true acid reflux. A structural problem such as a hiatus hernia may need specific treatment. Objective testing, on or off therapy depending on the diagnostic question, can clarify what is happening.',
          },
        },
        {
          question: { content: 'What is functional heartburn?' },
          answer: {
            content:
              'Functional heartburn is a condition in which a person experiences heartburn-type symptoms but oesophageal acid exposure is within the normal range and symptoms do not correlate with reflux episodes. The pain is real and generated through genuine sensory pathways, but treatment focuses on reducing oesophageal sensitivity rather than further acid suppression.',
          },
        },
        {
          question: {
            content: 'Is it safe to stay on acid reflux medication long-term?',
          },
          answer: {
            content:
              'For most people who genuinely need them, PPIs are an effective treatment with a well-characterised safety profile. Like all medicines, they can have side effects, and benefits and risks should be reviewed with a clinician. The aim is to use the lowest effective dose. A periodic review of whether ongoing medication is still required is sensible, and any decision to stop should be guided by a clinician.',
          },
        },
      ],
    },
  },
];
